import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Package, Eye, Clock, CreditCard, Banknote, CheckCircle, Truck,
  MapPin, Phone, Printer, ChefHat, Search, Users, ChevronDown, RotateCw,
  Pencil, XCircle, Minus, Plus, Trash2, AlertTriangle, BookOpen, User
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../ui/collapsible';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import LowStockAlert from './LowStockAlert';
import WaiterCallsBanner from '../WaiterCallsBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrdersManagement = ({ token, onPrint, printedOrders, onMarkPrinted, printCounts = {} }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('hazirlaniyor');
  const [channelFilter, setChannelFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [cancelConfirmId, setCancelConfirmId] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [pickerType, setPickerType] = useState('alakart'); // 'alakart' | 'beverage'
  const [pickerSearch, setPickerSearch] = useState('');
  const [menuItemsCache, setMenuItemsCache] = useState([]);
  const [beverageItemsCache, setBeverageItemsCache] = useState([]);

  // To-Veresiye state
  const [veresiyeOrder, setVeresiyeOrder] = useState(null); // order being moved to veresiye
  const [veresiyeCustomers, setVeresiyeCustomers] = useState([]);
  const [veresiyeCustSearch, setVeresiyeCustSearch] = useState('');
  const [veresiyeSelectedCust, setVeresiyeSelectedCust] = useState(null); // {customer_name, customer_phone}
  const [veresiyeNewName, setVeresiyeNewName] = useState('');
  const [veresiyeNewPhone, setVeresiyeNewPhone] = useState('');
  const [veresiyeNotes, setVeresiyeNotes] = useState('');
  const [veresiyeMode, setVeresiyeMode] = useState('existing'); // 'existing' | 'new'
  const [veresiyeSubmitting, setVeresiyeSubmitting] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchOrders = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (channelFilter !== 'all') params.append('channel', channelFilter);
      const qs = params.toString();
      const response = await axios.get(`${API}/admin/orders${qs ? `?${qs}` : ''}`, { headers });
      setOrders(response.data);
    } catch (error) {
      console.error('Orders fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, channelFilter, token]);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 10000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const handlePrint = (order, type) => {
    onPrint(order, type);
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status: newStatus }, { headers });
      toast.success('Durum güncellendi');
      fetchOrders();
    } catch (error) {
      toast.error('Hata oluştu');
    }
  };

  const handleCancelOrder = async (orderId) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/orders/${orderId}/cancel`, {}, { headers });
      toast.success('Sipariş iptal edildi');
      setCancelConfirmId(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İptal işlemi başarısız');
    } finally { setSaving(false); }
  };

  const openVeresiyeDialog = async (order) => {
    setVeresiyeOrder(order);
    setVeresiyeCustSearch('');
    setVeresiyeSelectedCust(null);
    setVeresiyeNewName(order.user_name && order.user_name !== 'Misafir' ? order.user_name : '');
    setVeresiyeNewPhone(order.user_phone || '');
    setVeresiyeNotes('');
    setVeresiyeMode('existing');
    try {
      const res = await axios.get(`${API}/admin/veresiye/customers`, { headers });
      setVeresiyeCustomers(res.data || []);
    } catch {
      setVeresiyeCustomers([]);
    }
  };

  const closeVeresiyeDialog = () => {
    setVeresiyeOrder(null);
    setVeresiyeSelectedCust(null);
    setVeresiyeNewName('');
    setVeresiyeNewPhone('');
    setVeresiyeNotes('');
    setVeresiyeCustSearch('');
  };

  const handleTransferToVeresiye = async () => {
    if (!veresiyeOrder) return;
    let customer_name = '';
    let customer_phone = '';
    if (veresiyeMode === 'existing') {
      if (!veresiyeSelectedCust) return toast.error('Bir müşteri seçin');
      customer_name = veresiyeSelectedCust.customer_name;
      customer_phone = veresiyeSelectedCust.customer_phone || '';
    } else {
      customer_name = (veresiyeNewName || '').trim();
      customer_phone = (veresiyeNewPhone || '').trim();
      if (!customer_name) return toast.error('Müşteri adı gerekli');
    }
    setVeresiyeSubmitting(true);
    try {
      await axios.post(`${API}/admin/orders/${veresiyeOrder.id}/to-veresiye`, {
        customer_name,
        customer_phone,
        notes: veresiyeNotes || null
      }, { headers });
      toast.success(`Sipariş ${customer_name} hesabına veresiye olarak aktarıldı`);
      closeVeresiyeDialog();
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Veresiyeye aktarma başarısız');
    } finally { setVeresiyeSubmitting(false); }
  };

  const openEditDialog = async (order) => {
    setEditingOrder(order);
    setEditItems(order.items.map(item => ({ ...item })));
    setEditNotes(order.notes || '');
    // Lazy-fetch menu+beverages once
    if (menuItemsCache.length === 0 || beverageItemsCache.length === 0) {
      try {
        const [m, b] = await Promise.all([
          axios.get(`${API}/menu`),
          axios.get(`${API}/beverages`),
        ]);
        setMenuItemsCache(m.data || []);
        setBeverageItemsCache(b.data || []);
      } catch (e) {
        console.error('Menu/beverage fetch error:', e);
      }
    }
  };

  const addProductToEdit = (product, type) => {
    const newItem = {
      item_id: product.id || '',
      name: product.name,
      name_tr: product.name_tr || product.name,
      price: Number(product.price) || 0,
      quantity: 1,
      item_type: type === 'beverage' ? 'beverage' : 'alakart',
      catering_selections: null,
    };
    setEditItems(prev => [...prev, newItem]);
    toast.success(`${newItem.name_tr} eklendi`);
  };

  const updateEditItemQty = (idx, delta) => {
    setEditItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const step = item.item_type === 'beverage' ? 1 : 0.5;
      const min = item.item_type === 'beverage' ? 1 : 0.5;
      return { ...item, quantity: Math.max(min, item.quantity + delta * step) };
    }));
  };

  const removeEditItem = (idx) => {
    setEditItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSaveEdit = async () => {
    if (editItems.length === 0) {
      toast.error('Sipariş en az 1 ürün içermelidir');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/orders/${editingOrder.id}/edit`, {
        items: editItems,
        notes: editNotes
      }, { headers });
      toast.success(`Sipariş güncellendi. Yeni toplam: €${res.data.total_price?.toFixed(2)}`);
      setEditingOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Düzenleme başarısız');
    } finally { setSaving(false); }
  };

  const getStatusBadge = (status) => {
    const config = {
      hazirlaniyor: { bg: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Hazırlanıyor' },
      yolda: { bg: 'bg-blue-100 text-blue-700', icon: Truck, label: 'Yolda' },
      teslim_edildi: { bg: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Teslim Edildi' },
      iptal: { bg: 'bg-red-100 text-red-700', icon: XCircle, label: 'İptal Edildi' },
      veresiye_aktarildi: { bg: 'bg-purple-100 text-purple-700', icon: BookOpen, label: 'Veresiye' }
    };
    const c = config[status] || { bg: 'bg-gray-100', icon: Clock, label: status };
    const Icon = c.icon;
    return <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${c.bg}`}><Icon className="w-3 h-3" />{c.label}</span>;
  };

  const getChannelBadge = (order) => {
    if (order.channel === 'garson') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700"><ChefHat className="w-3 h-3" />Garson</span>;
    if (order.order_type === 'salon') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">Salon</span>;
    if (order.order_type === 'table') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-700">Masa</span>;
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700"><Package className="w-3 h-3" />Paket</span>;
  };

  if (loading) return <div className="text-center py-8">Yükleniyor...</div>;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
      <WaiterCallsBanner token={token} embedded />
      <LowStockAlert token={token} threshold={5} />
      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-playfair text-xl font-semibold text-charcoal">Sipariş Yönetimi</h2>
        <div className="flex gap-2">
          <Select value={channelFilter} onValueChange={setChannelFilter}>
            <SelectTrigger className="w-36" data-testid="channel-filter"><SelectValue placeholder="Kanal" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Kanallar</SelectItem>
              <SelectItem value="garson">Garson</SelectItem>
              <SelectItem value="masa">Masa (QR)</SelectItem>
              <SelectItem value="paket">Paket</SelectItem>
              <SelectItem value="salon">Salon</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="status-filter"><SelectValue placeholder="Filtrele" /></SelectTrigger>
            <SelectContent>
            <SelectItem value="all">Tümü</SelectItem>
            <SelectItem value="hazirlaniyor">Hazırlanıyor</SelectItem>
            <SelectItem value="yolda">Yolda</SelectItem>
            <SelectItem value="teslim_edildi">Teslim Edildi</SelectItem>
            <SelectItem value="iptal">İptal Edildi</SelectItem>
            <SelectItem value="veresiye_aktarildi">Veresiyeye Aktarılan</SelectItem>
          </SelectContent>
        </Select>
        </div>
      </div>

      {/* Orders Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {orders.map((order) => {
          const isUnprinted = !printedOrders.has(order.id) && order.status === 'hazirlaniyor';
          // Channel accent color for the top stripe
          const channel = order.channel || (order.order_type === 'delivery' ? 'paket' : order.order_type === 'salon' ? 'salon' : 'masa');
          const accentColors = {
            garson: 'from-amber-500 to-orange-600',
            masa: 'from-basil to-emerald-700',
            salon: 'from-blue-500 to-blue-700',
            paket: 'from-orange-500 to-red-600',
          };
          const accent = accentColors[channel] || 'from-stone-400 to-stone-500';
          return (
          <motion.div key={order.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -2 }}
            className={`bg-white rounded-xl shadow-sm border transition-all overflow-hidden ${
              isUnprinted
                ? 'border-terracotta border-2 animate-pulse shadow-lg shadow-terracotta/20'
                : 'border-stone-100 hover:shadow-lg hover:border-stone-200'
            }`}>
            <div className={`h-1 bg-gradient-to-r ${accent}`} />
            <div className="p-4">
            {isUnprinted && (
              <div className="bg-terracotta text-white text-xs font-bold px-2 py-1 rounded-full mb-2 inline-block animate-bounce" data-testid={`unprinted-badge-${order.id}`}>
                YENİ SİPARİŞ - ÇIKTI ALINMADI
              </div>
            )}
            {printCounts[order.id] > 0 && (
              <div className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full mb-2" data-testid={`print-count-badge-${order.id}`}>
                <Printer className="w-3 h-3" />
                {printCounts[order.id]}x Yazdırıldı
              </div>
            )}
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="font-mono text-sm font-bold text-charcoal tracking-tight">#{order.id.slice(-8).toUpperCase()}</p>
                <p className="text-xs text-charcoal/60 mt-0.5">{format(new Date(order.created_at), 'd MMM HH:mm', { locale: tr })}</p>
                {getChannelBadge(order)}
              </div>
              {getStatusBadge(order.status)}
            </div>
            <div className="space-y-2 mb-3">
              <div className="flex items-center gap-2 text-sm">
                <Users className="w-4 h-4 text-charcoal/50" />
                <span className="font-medium">{order.user_name}</span>
              </div>
              {order.waiter_name && (
                <div className="flex items-center gap-2 text-sm text-purple-600">
                  <ChefHat className="w-4 h-4" />
                  <span className="font-medium">{order.waiter_name}</span>
                </div>
              )}
              {order.table_number && (
                <div className="flex items-center gap-2 text-sm text-charcoal/70">
                  <span className="text-xs font-medium bg-stone-100 px-2 py-0.5 rounded">Masa {order.table_number}</span>
                </div>
              )}
              {order.user_phone && (
                <div className="flex items-center gap-2 text-sm text-charcoal/70">
                  <Phone className="w-4 h-4 text-charcoal/50" />
                  <span>{order.user_phone}</span>
                </div>
              )}
              {order.address?.district && (
                <div className="flex items-center gap-2 text-sm text-charcoal/70">
                  <MapPin className="w-4 h-4 text-charcoal/50" />
                  <span className="truncate">{order.address?.district}, {order.address?.city}</span>
                </div>
              )}
            </div>
            {/* Order Items List */}
            <Collapsible defaultOpen={true}>
              <CollapsibleTrigger className="flex items-center justify-between w-full pt-3 pb-2 border-t-2 border-stone-200 group cursor-pointer" data-testid={`order-items-toggle-${order.id}`}>
                <span className="text-sm font-bold text-charcoal uppercase tracking-wide">Sipariş ({order.items?.length || 0} kalem)</span>
                <ChevronDown className="w-4 h-4 text-charcoal/50 transition-transform group-data-[state=open]:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-1.5 py-1" data-testid={`order-items-list-${order.id}`}>
                  {order.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-amber-50/80 border border-amber-100 rounded-lg px-3 py-2">
                      <span className="text-sm font-semibold text-charcoal">
                        <span className="font-black text-base text-terracotta mr-1">{item.quantity % 1 === 0 ? item.quantity : item.quantity.toFixed(1)}x</span>
                        {item.name_tr || item.name}
                      </span>
                      <span className="font-bold text-sm text-charcoal whitespace-nowrap ml-3">€{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Total & Payment */}
            <div className="flex items-center justify-between pt-3 border-t-2 border-stone-200">
              <div className="flex items-center gap-1.5">
                {order.payment_method === 'nakit' ? <Banknote className="w-5 h-5 text-green-600" /> : <CreditCard className="w-5 h-5 text-blue-600" />}
                <span className="text-sm font-semibold text-charcoal/70">{order.payment_method === 'nakit' ? 'Nakit' : 'Kart'}</span>
              </div>
              <p className="font-playfair text-2xl font-bold bg-gradient-to-br from-terracotta to-orange-700 bg-clip-text text-transparent">€{order.total_price?.toFixed(2)}</p>
            </div>
            <div className="flex gap-1.5 mt-3 flex-wrap">
              {order.status !== 'iptal' && order.status !== 'veresiye_aktarildi' && (
                <>
                  <Select value={order.status} onValueChange={(val) => updateStatus(order.id, val)}>
                    <SelectTrigger className="flex-1 h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hazirlaniyor">Hazırlanıyor</SelectItem>
                      <SelectItem value="yolda">Yolda</SelectItem>
                      <SelectItem value="teslim_edildi">Teslim Edildi</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" onClick={() => openEditDialog(order)} title="Düzelt" data-testid={`order-edit-btn-${order.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openVeresiyeDialog(order)}
                    className="border-purple-200 text-purple-700 hover:bg-purple-50"
                    title="Veresiyeye Aktar" data-testid={`order-to-veresiye-btn-${order.id}`}>
                    <BookOpen className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setCancelConfirmId(order.id)} className="border-red-200 text-red-600 hover:bg-red-50" title="İptal Et" data-testid={`order-cancel-btn-${order.id}`}>
                    <XCircle className="w-4 h-4" />
                  </Button>
                </>
              )}
              <Button size="sm" variant="outline" onClick={() => setSelectedOrder(order)} data-testid={`order-detail-btn-${order.id}`}><Eye className="w-4 h-4" /></Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handlePrint(order, 'kitchen')}
                className="bg-amber-50 border-amber-200 hover:bg-amber-100 text-amber-700"
                title="Mutfak Fişi"
                data-testid={`print-kitchen-btn-${order.id}`}
              >
                <ChefHat className="w-4 h-4" />
              </Button>
              <Button 
                size="sm" 
                variant={isUnprinted ? "default" : "outline"}
                onClick={() => handlePrint(order, 'full')}
                className={isUnprinted ? "bg-terracotta hover:bg-terracotta/90 text-white" : ""}
                title="Adisyon Yazdır"
                data-testid={`print-receipt-btn-${order.id}`}
              >
                <Printer className="w-4 h-4" />
              </Button>
            </div>
            {order.status === 'iptal' && (
              <div className="mt-2 text-center text-sm font-semibold text-red-600 bg-red-50 rounded-lg py-2">
                Bu sipariş iptal edilmiştir
              </div>
            )}
            {order.status === 'veresiye_aktarildi' && (
              <div className="mt-2 text-center text-sm font-semibold text-purple-700 bg-purple-50 rounded-lg py-2 flex items-center justify-center gap-1.5" data-testid={`order-veresiye-info-${order.id}`}>
                <BookOpen className="w-4 h-4" />
                Veresiyeye aktarıldı{order.veresiye_customer_name ? ` — ${order.veresiye_customer_name}` : ''}
              </div>
            )}
            </div>
          </motion.div>
        );
        })}
      </div>

      {orders.length === 0 && <div className="text-center py-12 text-charcoal/60">Sipariş bulunamadı</div>}

      {/* Order Detail Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Sipariş Detayı</DialogTitle></DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-bold text-xl">#{selectedOrder.id?.slice(-8).toUpperCase()}</span>
                <div className="flex items-center gap-2">
                  {getChannelBadge(selectedOrder)}
                  {getStatusBadge(selectedOrder.status)}
                </div>
              </div>
              <div className="bg-sand rounded-lg p-4 space-y-2">
                <p><strong>Müşteri:</strong> {selectedOrder.user_name}</p>
                {selectedOrder.user_phone && <p><strong>Telefon:</strong> {selectedOrder.user_phone}</p>}
                <p><strong>Tarih:</strong> {format(new Date(selectedOrder.created_at), 'd MMMM yyyy HH:mm', { locale: tr })}</p>
                <p><strong>Ödeme:</strong> {selectedOrder.payment_method === 'nakit' ? 'Kapıda Nakit' : 'Kapıda Kart'}</p>
                {selectedOrder.table_number && <p><strong>Masa:</strong> {selectedOrder.table_number}</p>}
                {selectedOrder.waiter_note && <p><strong>Garson Notu:</strong> {selectedOrder.waiter_note}</p>}
              </div>
              <div>
                <h4 className="font-semibold mb-2">Sipariş İçeriği</h4>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, i) => (
                    <div key={`detail-${item.name}-${i}`} className="flex justify-between items-start bg-white p-3 rounded-lg border">
                      <div>
                        <p className="font-medium">{item.quantity % 1 === 0 ? item.quantity : item.quantity.toFixed(1)}x {item.name_tr || item.name}</p>
                        {item.catering_selections && (
                          <p className="text-xs text-charcoal/60 mt-1">
                            {Object.values(item.catering_selections).filter(s => s?.name).map(s => s.name).join(', ')}
                          </p>
                        )}
                      </div>
                      <span className="font-semibold">€{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-sand rounded-lg p-4">
                <p><strong>Adres:</strong> {selectedOrder.address?.title}</p>
                <p className="text-sm text-charcoal/70">{selectedOrder.address?.address}</p>
                <p className="text-sm text-charcoal/70">{selectedOrder.address?.district}, {selectedOrder.address?.city}</p>
              </div>
              {selectedOrder.notes && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <strong>Not:</strong> {selectedOrder.notes}
                </div>
              )}
              <div className="flex justify-between items-center pt-4 border-t">
                <span className="font-semibold">Toplam:</span>
                <span className="font-playfair text-2xl font-bold text-terracotta">€{selectedOrder.total_price?.toFixed(2)}</span>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => handlePrint(selectedOrder, 'kitchen')} className="flex-1 bg-amber-600 text-white hover:bg-amber-500" data-testid="detail-print-kitchen">
                  <ChefHat className="w-4 h-4 mr-2" />Mutfak Fişi
                </Button>
                <Button onClick={() => handlePrint(selectedOrder, 'full')} className="flex-1 bg-basil text-white hover:bg-basil/90" data-testid="detail-print-receipt">
                  <Printer className="w-4 h-4 mr-2" />Adisyon
                </Button>
              </div>
              {printCounts[selectedOrder.id] > 0 && (
                <p className="text-center text-xs text-charcoal/50">Bu sipariş {printCounts[selectedOrder.id]} kez yazdırıldı</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Cancel Confirmation Dialog */}
      <Dialog open={!!cancelConfirmId} onOpenChange={() => setCancelConfirmId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="w-5 h-5" />Sipariş İptali</DialogTitle></DialogHeader>
          <p className="text-charcoal/70">
            <strong>#{cancelConfirmId?.slice(-8).toUpperCase()}</strong> numaralı siparişi iptal etmek istediğinize emin misiniz? Bu işlem geri alınamaz.
          </p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" className="flex-1" onClick={() => setCancelConfirmId(null)} data-testid="cancel-dialog-no">Vazgeç</Button>
            <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={() => handleCancelOrder(cancelConfirmId)} disabled={saving} data-testid="cancel-dialog-yes">
              {saving ? 'İptal Ediliyor...' : 'Evet, İptal Et'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* To-Veresiye Dialog */}
      <Dialog open={!!veresiyeOrder} onOpenChange={(o) => { if (!o) closeVeresiyeDialog(); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <BookOpen className="w-5 h-5" />Siparişi Veresiyeye Aktar
            </DialogTitle>
          </DialogHeader>
          {veresiyeOrder && (
            <div className="space-y-4">
              {/* Order summary */}
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-charcoal">#{veresiyeOrder.id?.slice(-8).toUpperCase()}</span>
                  <span className="font-bold text-lg text-purple-700">€{veresiyeOrder.total_price?.toFixed(2)}</span>
                </div>
                <p className="text-xs text-charcoal/60 mt-1">
                  {veresiyeOrder.items?.length} kalem · {veresiyeOrder.user_name || 'Misafir'}
                </p>
              </div>

              {/* Mode switcher */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setVeresiyeMode('existing')}
                  className={`flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${veresiyeMode === 'existing' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-stone-200 text-charcoal/60'}`}
                  data-testid="veresiye-mode-existing">
                  <Users className="w-4 h-4" />Kayıtlı Müşteri
                </button>
                <button
                  onClick={() => setVeresiyeMode('new')}
                  className={`flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${veresiyeMode === 'new' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-stone-200 text-charcoal/60'}`}
                  data-testid="veresiye-mode-new">
                  <Plus className="w-4 h-4" />Yeni Müşteri
                </button>
              </div>

              {veresiyeMode === 'existing' ? (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 w-4 h-4 text-charcoal/40" />
                    <Input
                      placeholder="Müşteri ara (isim veya telefon)..."
                      value={veresiyeCustSearch}
                      onChange={e => setVeresiyeCustSearch(e.target.value)}
                      className="pl-10"
                      data-testid="veresiye-cust-search"
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto border border-stone-200 rounded-xl divide-y divide-stone-100">
                    {veresiyeCustomers.length === 0 ? (
                      <p className="text-center py-6 text-sm text-charcoal/40">Kayıtlı müşteri yok</p>
                    ) : (
                      veresiyeCustomers
                        .filter(c => !veresiyeCustSearch ||
                          c.customer_name.toLowerCase().includes(veresiyeCustSearch.toLowerCase()) ||
                          (c.customer_phone || '').includes(veresiyeCustSearch)
                        )
                        .map((c, i) => {
                          const isSel = veresiyeSelectedCust?.customer_name === c.customer_name &&
                            (veresiyeSelectedCust?.customer_phone || '') === (c.customer_phone || '');
                          return (
                            <button
                              key={`${c.customer_name}-${c.customer_phone || ''}-${i}`}
                              onClick={() => setVeresiyeSelectedCust(c)}
                              className={`w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-purple-50 transition-colors ${isSel ? 'bg-purple-100' : ''}`}
                              data-testid={`veresiye-cust-row-${i}`}>
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="w-8 h-8 rounded-full bg-purple-200 text-purple-700 flex items-center justify-center text-xs font-bold">
                                  {(c.customer_name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                  <p className="font-semibold text-sm text-charcoal truncate">{c.customer_name}</p>
                                  {c.customer_phone && <p className="text-xs text-charcoal/50 truncate">{c.customer_phone}</p>}
                                </div>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <p className={`text-sm font-bold ${(c.balance || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                  €{(c.balance || 0).toFixed(2)}
                                </p>
                                <p className="text-[10px] text-charcoal/40 uppercase">{(c.balance || 0) > 0 ? 'Borç' : 'Kapalı'}</p>
                              </div>
                            </button>
                          );
                        })
                    )}
                  </div>
                  {veresiyeSelectedCust && (
                    <div className="text-xs bg-green-50 border border-green-200 text-green-700 rounded-lg px-3 py-2" data-testid="veresiye-selected-info">
                      Seçili: <strong>{veresiyeSelectedCust.customer_name}</strong>
                      {veresiyeSelectedCust.customer_phone ? ` · ${veresiyeSelectedCust.customer_phone}` : ''}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <div>
                    <label className="text-xs font-medium text-charcoal/70 block mb-1">Müşteri Adı *</label>
                    <Input
                      value={veresiyeNewName}
                      onChange={e => setVeresiyeNewName(e.target.value)}
                      placeholder="Ad Soyad"
                      data-testid="veresiye-new-name"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-charcoal/70 block mb-1">Telefon (opsiyonel)</label>
                    <Input
                      value={veresiyeNewPhone}
                      onChange={e => setVeresiyeNewPhone(e.target.value)}
                      placeholder="+382 ..."
                      data-testid="veresiye-new-phone"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs font-medium text-charcoal/70 block mb-1">Not (opsiyonel)</label>
                <Input
                  value={veresiyeNotes}
                  onChange={e => setVeresiyeNotes(e.target.value)}
                  placeholder="Veresiye notu..."
                  data-testid="veresiye-notes"
                />
              </div>

              <div className="flex gap-2 pt-2 border-t border-stone-200">
                <Button variant="outline" className="flex-1" onClick={closeVeresiyeDialog} data-testid="veresiye-cancel">Vazgeç</Button>
                <Button
                  className="flex-1 bg-purple-700 hover:bg-purple-600 text-white"
                  onClick={handleTransferToVeresiye}
                  disabled={veresiyeSubmitting}
                  data-testid="veresiye-confirm">
                  {veresiyeSubmitting ? 'Aktarılıyor...' : (<><BookOpen className="w-4 h-4 mr-1" />Veresiyeye Aktar</>)}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Order Dialog */}
      <Dialog open={!!editingOrder} onOpenChange={(open) => { if (!open) { setEditingOrder(null); setShowProductPicker(false); setPickerSearch(''); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-terracotta" />Sipariş Düzelt — #{editingOrder?.id?.slice(-8).toUpperCase()}</DialogTitle></DialogHeader>
          {editingOrder && (
            <div className="space-y-4">
              <div className="space-y-2">
                {editItems.map((item, idx) => (
                  <div key={`edit-${item.name}-${idx}`} className="flex items-center gap-2 bg-stone-50 rounded-lg p-3 border">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{item.name_tr || item.name}</p>
                      <p className="text-xs text-charcoal/50">€{item.price?.toFixed(2)} / adet</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => updateEditItemQty(idx, -1)} className="w-7 h-7 rounded-full bg-white border flex items-center justify-center hover:bg-stone-100" data-testid={`edit-qty-minus-${idx}`}>
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-8 text-center font-bold text-sm" data-testid={`edit-qty-value-${idx}`}>
                        {item.quantity % 1 === 0 ? item.quantity : item.quantity.toFixed(1)}
                      </span>
                      <button onClick={() => updateEditItemQty(idx, 1)} className="w-7 h-7 rounded-full bg-white border flex items-center justify-center hover:bg-stone-100" data-testid={`edit-qty-plus-${idx}`}>
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <span className="font-bold text-sm w-16 text-right">€{(item.price * item.quantity).toFixed(2)}</span>
                    <button onClick={() => removeEditItem(idx)} className="w-7 h-7 rounded-full text-red-500 hover:bg-red-50 flex items-center justify-center" data-testid={`edit-remove-${idx}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {editItems.length === 0 && <p className="text-center text-red-500 text-sm py-4">Tüm ürünler kaldırıldı. En az 1 ürün gerekli.</p>}
              </div>

              {/* Add Product Section */}
              <div className="border-t pt-3">
                {!showProductPicker ? (
                  <Button variant="outline" className="w-full border-dashed border-terracotta text-terracotta hover:bg-terracotta/5" onClick={() => setShowProductPicker(true)} data-testid="edit-add-product-btn">
                    <Plus className="w-4 h-4 mr-2" />Ürün Ekle
                  </Button>
                ) : (
                  <div className="bg-stone-50 rounded-lg p-3 border space-y-2">
                    <div className="flex gap-2">
                      <button onClick={() => setPickerType('alakart')} className={`flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-colors ${pickerType === 'alakart' ? 'bg-terracotta text-white' : 'bg-white border'}`} data-testid="picker-tab-alakart">A La Carte</button>
                      <button onClick={() => setPickerType('beverage')} className={`flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-colors ${pickerType === 'beverage' ? 'bg-terracotta text-white' : 'bg-white border'}`} data-testid="picker-tab-beverage">İçecek</button>
                      <button onClick={() => { setShowProductPicker(false); setPickerSearch(''); }} className="px-2 text-charcoal/60 hover:text-charcoal" title="Kapat">
                        <XCircle className="w-5 h-5" />
                      </button>
                    </div>
                    <Input placeholder="Ürün ara..." value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)} data-testid="picker-search-input" />
                    <div className="max-h-56 overflow-y-auto space-y-1">
                      {(pickerType === 'alakart' ? menuItemsCache : beverageItemsCache)
                        .filter(p => {
                          const q = pickerSearch.toLowerCase().trim();
                          if (!q) return true;
                          return (p.name_tr || '').toLowerCase().includes(q) || (p.name || '').toLowerCase().includes(q);
                        })
                        .slice(0, 100)
                        .map((p) => (
                          <button
                            key={`pick-${p.id}`}
                            onClick={() => addProductToEdit(p, pickerType)}
                            className="w-full flex items-center justify-between gap-2 bg-white hover:bg-terracotta/10 border rounded-md px-3 py-2 text-left transition-colors"
                            data-testid={`picker-item-${p.id}`}
                          >
                            <span className="text-sm font-medium truncate">{p.name_tr || p.name}</span>
                            <span className="text-sm font-bold text-terracotta whitespace-nowrap">€{Number(p.price).toFixed(2)}</span>
                          </button>
                        ))}
                      {((pickerType === 'alakart' ? menuItemsCache : beverageItemsCache).length === 0) && (
                        <p className="text-center text-charcoal/50 text-sm py-4">Yükleniyor...</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-charcoal/70 mb-1 block">Not</label>
                <Input value={editNotes} onChange={(e) => setEditNotes(e.target.value)} placeholder="Sipariş notu..." data-testid="edit-notes-input" />
              </div>
              <div className="flex justify-between items-center pt-3 border-t">
                <span className="font-semibold">Yeni Toplam:</span>
                <span className="font-playfair text-xl font-bold text-terracotta">
                  €{editItems.reduce((sum, item) => sum + item.price * item.quantity, 0).toFixed(2)}
                </span>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setEditingOrder(null)}>Vazgeç</Button>
                <Button className="flex-1 bg-terracotta hover:bg-terracotta/90 text-white" onClick={handleSaveEdit} disabled={saving || editItems.length === 0} data-testid="edit-save-btn">
                  {saving ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default OrdersManagement;
